<template>
    <div v-if="reassuranceItems.length" class="hero-reassurance" data-testid="hero-reassurance">
        <span
            v-for="(item, index) in reassuranceItems"
            :key="index"
            :class="['hero-reassurance__item', {'hero-reassurance__item--separated': item.hasSeparator}]"
        >
            <span v-if="item.showsRating" class="hero-reassurance__stars">
                <VJoyIcon v-for="star in RATING_STARS" :key="star" name="star-fill" color="quaternary" size="xsmall" />
            </span>
            <PrismicImage v-else :field="item.image" class="hero-reassurance__image" />
            <PrismicRichText v-if="item.hasText" :field="item.text" class="hero-reassurance__text" />
        </span>
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {asText, isFilled} from '@prismicio/helpers';
    import {PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {HeroSliceHeroWithVideoPrimaryReassuranceItem} from '@prismicio-types';
    import {VJoyIcon} from '@maltjoy/core-vue';

    const RATING_STARS = [1, 2, 3, 4, 5];

    const {items} = defineProps<{
        items: HeroSliceHeroWithVideoPrimaryReassuranceItem[];
    }>();

    const reassuranceItems = computed(() => {
        const filledItems = items.filter((item) => isFilled.image(item.image) || isFilled.richText(item.text));

        return filledItems.map((item, index) => {
            const hasText = isFilled.richText(item.text);
            const nextItem = filledItems[index + 1];

            return {
                hasText,
                hasSeparator: !!nextItem,
                image: item.image,
                showsRating: !isFilled.image(item.image) && hasText && asText(item.text).includes('/5'),
                text: item.text,
            };
        });
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .hero-reassurance {
        --hero-reassurance-gap: var(--joy-core-spacing-9);

        display: grid;
        padding-top: 40px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-template-rows: repeat(2, auto);
        gap: var(--hero-reassurance-gap);
        width: 100%;

        @include mq.screen_md(min) {
            @include text.getFontBySize(primary-400);

            display: flex;
            justify-content: center;
            flex-wrap: wrap;
        }

        &__item {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-3);
            justify-content: center;

            &--separated {
                position: relative;

                &::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    right: calc(var(--hero-reassurance-gap) / -2);
                    width: 1px;
                    height: 50px;
                    background-color: var(--joy-color-neutral-30);
                    transform: translate(50%, -50%);
                }
            }
        }

        &__stars {
            display: inline-flex;
            align-items: center;
            gap: var(--joy-core-spacing-0);
        }

        &__image {
            display: block;
            width: auto;
            height: 36px;
            object-fit: contain;
        }

        &__text {
            line-height: var(--joy-line-height-small);

            strong {
                color: var(--joy-color-neutral-60);
            }
        }
    }
</style>
